import { proxy } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'
import { useStoreKeySelector } from '@/lib/valtio/store-hooks'

type CueUiState = {
  /** Ids of groups whose children are hidden. */
  collapsedGroups: string[]

  /** Ids selected for a batch operation, in clicked order. */
  selectedCueIds: string[]
}

const initialState: CueUiState = {
  collapsedGroups: [],
  selectedCueIds: [],
}

/**
 * UI state for cues: which cue groups are collapsed, which cues are batch
 * selected.
 *
 * @remarks
 *   Local UI state, never persisted and never sent to the API. It lives next to
 *   the cue data rather than in a generic UI store because the derived cue list
 *   reads `collapsedGroups` (see `visibleIndexedCues` in `cue.derived.ts`), and
 *   a derivation cannot span two state libraries.
 */
const cueUiStore = proxy<CueUiState>(deepClone(initialState))

export const cueUiActions = {
  /** Collapse a group, or expand it if it is already collapsed. */
  toggleGroup(cueId: string) {
    const { collapsedGroups } = cueUiStore

    cueUiStore.collapsedGroups = collapsedGroups.includes(cueId)
      ? collapsedGroups.filter((id) => id !== cueId)
      : [...collapsedGroups, cueId]
  },

  /**
   * Expand a group, whatever state it is in.
   *
   * Separate from {@link toggleGroup} because the caller that needs it (reveal
   * the group holding the cue that is about to run) wants a guarantee, not a
   * flip: toggling a group that happened to be open would hide the next cue.
   */
  expandGroup(cueId: string) {
    const { collapsedGroups } = cueUiStore
    if (!collapsedGroups.includes(cueId)) return

    cueUiStore.collapsedGroups = collapsedGroups.filter((id) => id !== cueId)
  },

  /**
   * Collapse every group, or expand all of them.
   *
   * @param groupIds Ids to collapse. Passed in rather than derived here, so
   *   this store stays independent of the derived cue list.
   */
  toggleAllGroups(groupIds: string[], collapsed: boolean) {
    cueUiStore.collapsedGroups = collapsed ? [...groupIds] : []
  },

  /** Add or remove a cue from the batch selection. */
  toggleSelected(cueId: string) {
    const { selectedCueIds } = cueUiStore

    cueUiStore.selectedCueIds = selectedCueIds.includes(cueId)
      ? selectedCueIds.filter((id) => id !== cueId)
      : [...selectedCueIds, cueId]
  },

  /** Replace the batch selection outright. */
  setSelected(cueIds: string[]) {
    cueUiStore.selectedCueIds = [...cueIds]
  },

  clearSelected() {
    cueUiStore.selectedCueIds = []
  },

  /** Called when leaving a rundown. */
  resetStore() {
    resetStore(cueUiStore, initialState)
  },
}

/**
 * The store itself, for the derived layer and the store hooks.
 *
 * Read-only by type, so the single-entry-point rule above survives review.
 */
export const cueUiStoreReadonly: Readonly<CueUiState> = cueUiStore

// =============== HOOKS

/**
 * Is this group collapsed?
 *
 * Resolves to a boolean before React compares, so a component re-renders only
 * when its own group toggles.
 */
export function useIsGroupCollapsed(cueId: string): boolean {
  return useStoreKeySelector(cueUiStore, 'collapsedGroups', (collapsedGroups) =>
    collapsedGroups.includes(cueId),
  )
}

/**
 * Is this cue part of the batch selection?
 *
 * Narrow, see {@link useIsGroupCollapsed}.
 */
export function useIsCueSelected(cueId?: string | null): boolean {
  return useStoreKeySelector(cueUiStore, 'selectedCueIds', (selectedCueIds) =>
    cueId ? selectedCueIds.includes(cueId) : false,
  )
}

/**
 * The whole batch selection.
 *
 * For the toolbar which needs the ids themselves.
 */
export function useSelectedCueIds(): readonly string[] {
  return useStoreKeySelector(
    cueUiStore,
    'selectedCueIds',
    (selectedCueIds) => selectedCueIds,
  )
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(cueUiStore, { name: 'cueUiStore', enabled: true })
}
// #endregion
